'use client';

import { useEffect, useRef, useState } from 'react';
import {
  HudContextProvider,
  HudExtrapolatedGraphBlock,
  HudGlobeBlock,
} from '@/app/playbook/hud/shared/playbookHUDInfra';
import { BackgroundPatternSettingsProvider } from '@/app/playbook/extrapolated-graph/BackgroundPatternSettingsProvider';
import {
  sanitizeBackgroundPatternSettings,
  type BackgroundPatternSettings,
} from '@/app/playbook/extrapolated-graph/backgroundPatternStore';
import savedBackgroundPatternSettings from '@/app/playbook/extrapolated-graph/savedBackgroundPatternSettings.json';
import { EmploymentRate } from '@/app/playbook/indicators/EmploymentRate';
import { MedianIncome } from '@/app/playbook/indicators/MedianIncome';
import { AlignmentResearcherCount } from '@/app/playbook/indicators/AlignmentResearcherCount';
import { SafetyTaxIndicator } from '@/app/playbook/indicators/SafetyTaxIndicator';
import { DistributionOfPowerBubbles4Fade } from '@/app/playbook/hud/blocks/power/DistributionOfPowerBubbles4Fade';
import { LaborDotStripLeaderRows } from '@/app/playbook/labor-mix/dot-strip/LaborDotStripLeaderRows';
import type { PlanAPhaseData } from '@/data/plan-a/planATypes';
import type { Globe2Settings } from '@/app/playbook/globe/globe2Types';
import type { PlanId } from '@/lib/branchTypes';
import { isGlobeTopologyLoaded } from '@/app/playbook/globe/useContinents';

const BG_SETTINGS: BackgroundPatternSettings = sanitizeBackgroundPatternSettings(
  savedBackgroundPatternSettings as Partial<BackgroundPatternSettings>,
);

// Everything renders NATIVELY at the gutter width — no capture-time transform
// scaling (that approach left broken half-scaled output whenever the render
// script died mid-run). The width is the sidenote column (61mm ≈ 230.5px)
// MINUS 2px: the panel anchors at the slot's left edge (aligned with the
// notes' left edge), and the 2px slack on the right keeps the right-aligned
// mono values ("62%", "×1 speed") — whose ink runs ~0.75px past their boxes
// under print font metrics — clear of the page's content clip, which
// otherwise shaves them (the "1px cut off the HUD" bug).
const PANEL_WIDTH = 228.5;
const GRAPH_HEIGHT = 193;
const GLOBE_SIZE = 200;

/**
 * Chapter side panels for the print scenario, mirroring the live HUD but split
 * per the print design:
 *  - `hero` (first chapter only): the graph + dither gradient + WebGL globe —
 *    the part of the HUD that barely changes over the scenario — plus the data
 *    block. No PoliciesPanel (meaningless on paper without hover tooltips).
 *  - non-hero (every later year boundary): ONLY the data block (indicator
 *    metrics, power treemap, labor mix) — the part that actually changes.
 */
export function PrintSidePanel({
  date,
  plan,
  playbookData,
  globeSettings,
  hero = false,
}: {
  date: Date;
  plan: PlanId;
  playbookData: PlanAPhaseData[];
  // Same settings the live playbook's globe variant uses; Globe2 renders
  // nothing without them.
  globeSettings?: Globe2Settings;
  hero?: boolean;
}) {
  // The WebGL globe canvas doesn't survive printing — drawing buffers aren't
  // rasterized into print output by page.pdf() OR a plain CMD+P. So the page
  // makes ITSELF printable: once the globe has demonstrably painted (topology
  // data loaded, canvas non-blank and pixel-stable across consecutive
  // samples — the first frames are a bare sphere), snapshot it via
  // toDataURL (readable thanks to preserveDrawingBuffer) and swap the live
  // canvas for a plain <img> THROUGH React (raw DOM swaps get resurrected by
  // R3F re-renders), positioned exactly where the canvas was. The PDF script
  // just waits for img[data-globe-print-snapshot]; CMD+P is ready when the
  // on-screen "preparing" badge disappears.
  const heroBlockRef = useRef<HTMLDivElement | null>(null);
  const [globeSnap, setGlobeSnap] = useState<{
    url: string;
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    if (!hero || globeSnap) return;
    let prevHash: number | null = null;
    let stable = 0;
    const interval = setInterval(() => {
      const heroEl = heroBlockRef.current;
      const canvas = heroEl?.querySelector<HTMLCanvasElement>(
        '[data-globe-geometry-source] canvas',
      );
      if (!heroEl || !canvas || canvas.width === 0 || !isGlobeTopologyLoaded()) {
        return;
      }
      let hash: number | null = null;
      try {
        const probe = document.createElement('canvas');
        probe.width = 48;
        probe.height = 48;
        const ctx = probe.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(canvas, 0, 0, 48, 48);
        const d = ctx.getImageData(0, 0, 48, 48).data;
        let painted = 0;
        for (let i = 3; i < d.length; i += 4) if (d[i] > 16) painted++;
        if (painted >= 115) {
          // ≥5% coverage: actually painted. Hash for the stability check.
          let h = 0;
          for (let i = 0; i < d.length; i += 16) h = (h * 31 + d[i]) | 0;
          hash = h;
        }
      } catch {
        return;
      }
      stable = hash !== null && hash === prevHash ? stable + 1 : 0;
      prevHash = hash;
      if (stable < 2) return;
      const canvasRect = canvas.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      setGlobeSnap({
        url: canvas.toDataURL('image/png'),
        left: canvasRect.left - heroRect.left,
        top: canvasRect.top - heroRect.top,
        width: canvasRect.width,
        height: canvasRect.height,
      });
    }, 300);
    return () => clearInterval(interval);
  }, [hero, globeSnap]);

  return (
    <HudContextProvider
      currentDate={date}
      planAData={playbookData}
      selectedPlan={null}
      activePlan={plan}
      hoveredPlan={null}
      pageTextColor="#000"
      pageBackgroundColor="#fffff8"
      globeSettings={globeSettings}
      legendMarkerPositions={[]}
      selectedPath={[]}
      isAgentPopulationHidden={false}
      isPolicyGraphHidden={false}
      onNodeSelect={() => {}}
      showGalaxy={false}
      isEpilogueExpanded={false}
      isEpilogueScrolledTo={false}
      isHoverPreviewActive={false}
    >
      <BackgroundPatternSettingsProvider initialSettings={BG_SETTINGS} persist={false}>
        <div className="print-side-panel relative" style={{ width: PANEL_WIDTH }}>
          {hero ? (
            <div ref={heroBlockRef} className="print-hero-graph relative">
              {/* Globe fully inside the block (no negative offsets — Chromium
                  print drops negatively-offset absolute content). Everything
                  else here is DOM + 2D canvas, which prints natively; only
                  the WebGL globe needs the snapshot swap. */}
              {globeSnap ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  data-globe-print-snapshot
                  src={globeSnap.url}
                  alt=""
                  className="absolute z-[10] pointer-events-none"
                  style={{
                    left: globeSnap.left,
                    top: globeSnap.top,
                    width: globeSnap.width,
                    height: globeSnap.height,
                  }}
                />
              ) : (
                <HudGlobeBlock
                  className="absolute left-0 top-0 flex items-start z-[10] pointer-events-none"
                  size={GLOBE_SIZE}
                  minDevicePixelRatio={2}
                />
              )}
              <div
                className="font-mono text-[12px] w-full"
                style={{ maxWidth: PANEL_WIDTH }}
              >
                <HudExtrapolatedGraphBlock
                  width={PANEL_WIDTH}
                  height={GRAPH_HEIGHT}
                  numCapabilityThresholdLabels={0}
                  redAboveLossOfControl
                  showAllYears={false}
                  showYearDots={false}
                  showRiskBackgroundLabels={false}
                />
              </div>
            </div>
          ) : null}

          {/* Screen-only readiness signal: CMD+P before the swap would print
              a blank globe, so say when the page is print-ready. */}
          {hero && !globeSnap ? (
            <div className="fixed bottom-3 left-3 z-50 border border-[#8B0000] bg-[#fffff8] px-3 py-1.5 font-mono text-[11px] text-[#8B0000] print:hidden">
              Preparing globe for print — ⌘P once this disappears
            </div>
          ) : null}

          {/* Anchor year for the data-only panels — without the hero graph
              (whose axis shows its years), nothing else dates the insert. */}
          {!hero ? (
            <div className="mt-1 mb-1.5 font-mono text-[10px] font-bold leading-none">
              {date.getUTCFullYear()}
            </div>
          ) : null}

          {/* The per-year data block: the part of the HUD that changes. */}
          <div
            className={`print-hud-stats flex flex-col gap-0.5 font-mono ${hero ? 'mt-3' : ''}`}
          >
            <EmploymentRate oneLine />
            <MedianIncome oneLine />
            <AlignmentResearcherCount oneLine label="Alignment Researchers" units="" />
            <SafetyTaxIndicator oneLine />
          </div>

          <DistributionOfPowerBubbles4Fade
            className="mt-3"
            height={58}
            strokeColor="#000"
            iconColor="#000"
            backgroundColor="transparent"
            maxWidth={PANEL_WIDTH}
            gapPx={1}
          />

          {/* Same labor visualization as the live HUD (dot strip + legend),
              frozen to this chapter's date. Stacked legend rows — the HUD's
              inline layout needs its ~520px column and overflows this 228px
              panel. Fixed height cap (no responsiveMaxHeight) — print has no
              screen budget. */}
          <div className="mt-3">
            <LaborDotStripLeaderRows legendLayout="rows" />
          </div>
        </div>
      </BackgroundPatternSettingsProvider>
    </HudContextProvider>
  );
}
